import { useState, useEffect } from "react";
import { Offer } from "../../../models/offers";
import { getEmail } from "../../../api/users";
import { sendSupportEmail } from "../../../api/mail";
import toast from "react-hot-toast"

interface InquireModalProps {
    offer: Offer;
    isOpen: boolean;
    onClose: () => void;
}

export default function InquireModal({ offer, isOpen, onClose }: InquireModalProps) {
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [senderEmail, setSenderEmail] = useState("");

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchEmail = async () => {
            try {
                const data = await getEmail();
                setSenderEmail(data.email || "");
            } catch (err) {
                console.error("Failed to fetch email:", err);
            }
        };
        fetchEmail();
    }, []);

    useEffect(() => {
        if (offer) {
            setSubject(`RPM Marketplace - Inquiry about offer: ${offer.category}`);
            setBody(`Hi,\n\nI am interested in the offer "${offer.category}" (${offer.vertical}).\n\nDetails:\n- Payout: ${offer.payout_type === "fixed" ? `$${offer.payout}` : "Variable"}\n- Duration: ${offer.connected_duration ?? "N/A"}\n\nPlease let me know more.\n\nThanks!`);
        }
    }, [offer]);

    const handleSubmit = async () => {
        if (!senderEmail) {
            toast.error("Email not loaded yet");
            return;
        }
        setLoading(true);
        try {
            await sendSupportEmail({ subject, body, sender_email: senderEmail, admin_email: offer.offer_creator });
            toast.success("Inquiry sent successfully!");
            onClose();

        } catch (err) {
            console.error(err);
            toast.error("Failed to load your email");

        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-lg max-w-xl w-full p-6 relative">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 font-bold text-lg"
                >
                    ×
                </button>

                <h2 className="text-2xl font-semibold text-gray-800 mb-2">Inquire About Offer</h2>
                <p className="text-sm text-gray-500 mb-4">
                    Pre-filled info from the selected offer
                </p>

                <div className="flex flex-col gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                        <input
                            type="text"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                        <textarea
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm h-36 resize-none focus:outline-none focus:ring-2 focus:ring-orange-500"
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                        />
                    </div>

                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="bg-orange-500 text-white font-medium py-2 px-4 rounded-lg hover:bg-orange-600 transition"
                    >
                        {loading ? "Sending..." : "Send Email"}
                    </button>
                </div>
            </div>
        </div>
    );
}
